// src/login/user/UserProfile.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './UserProfile.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.jmkinfosoft.com';

// ✅ Notice Period Options
const NOTICE_PERIOD_OPTIONS = [
  { value: '', label: 'Select Notice Period' },
  { value: '0', label: 'Immediate (0 days)' },
  { value: '7', label: '7 days' },
  { value: '15', label: '15 days' },
  { value: '30', label: '30 days' },
  { value: '45', label: '45 days' },
  { value: '60', label: '60 days' },
  { value: '90', label: '90 days' },
  { value: '120', label: '120 days' }
];

const UserProfile = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeFileName, setResumeFileName] = useState('');
  const [userRole, setUserRole] = useState('candidate');
  
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    mobile: '',
    location: '',
    preferred_location: '',
    experience_years: '',
    education: '',
    skills: [],
    department: '',
    current_company: '',
    current_salary: '',
    expected_salary: '',
    notice_period_days: '',
    linkedin_url: '',
    resume_url: '',
    resume_key: '',
    resume_filename: '',
    source: '',
    extra: {},
  });

  const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
  });

  axiosInstance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('userRole') || 'candidate';
    
    setUserRole(role);
    
    if (!token) {
      navigate('/login');
      return;
    }

    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setIsFetching(true);
    setError('');
    try {
      const response = await axiosInstance.get('/api/candidates/me');
      console.log('📥 Profile data:', response.data);
      
      if (response.data.success) {
        const data = response.data.data;
        setProfile({
          name: data.name || '',
          email: data.email || '',
          mobile: data.mobile || '',
          location: data.location || '',
          preferred_location: data.preferred_location || '',
          experience_years: data.experience_years || '',
          education: data.education || '',
          skills: data.skills || [],
          department: data.department || '',
          current_company: data.current_company || '',
          current_salary: data.current_salary || '',
          expected_salary: data.expected_salary || '',
          notice_period_days: data.notice_period_days || '',
          linkedin_url: data.linkedin_url || '',
          resume_url: data.resume_url || '',
          resume_key: data.resume_key || '',
          resume_filename: data.resume_filename || '',
          source: data.source || '',
          extra: data.extra || {},
        });

        localStorage.setItem('userName', data.name || '');
        localStorage.setItem('userEmail', data.email || '');
        localStorage.setItem('userMobile', data.mobile || '');
      }
    } catch (err) {
      console.error('❌ Error fetching profile:', err);
      
      const name = localStorage.getItem('userName') || 'User';
      const email = localStorage.getItem('userEmail') || '';
      const mobile = localStorage.getItem('userMobile') || '';
      
      setProfile({
        ...profile,
        name: name,
        email: email,
        mobile: mobile,
      });
      
      setError('Could not fetch profile data. Showing saved data.');
    } finally {
      setIsFetching(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSkillsChange = (e) => {
    const value = e.target.value;
    const skillsArray = value ? value.split(',').map(s => s.trim()).filter(s => s) : [];
    setProfile((prev) => ({
      ...prev,
      skills: skillsArray
    }));
    setError('');
  };

  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const maxSize = 10 * 1024 * 1024;
      
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a PDF, DOC, or DOCX file');
        return;
      }
      
      if (file.size > maxSize) {
        setError('File size must be less than 10MB');
        return;
      }
      
      setResumeFile(file);
      setResumeFileName(file.name);
      setError('');
    }
  };

  const handleRemoveResume = () => {
    setResumeFile(null);
    setResumeFileName('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      if (!profile.name || !profile.mobile) {
        setError('Name and Mobile are required fields');
        setIsLoading(false);
        return;
      }

      const mobileRegex = /^[0-9]{10}$/;
      if (!mobileRegex.test(profile.mobile)) {
        setError('Please enter a valid 10-digit mobile number');
        setIsLoading(false);
        return;
      }

      if (profile.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) {
        setError('Please enter a valid email address');
        setIsLoading(false);
        return;
      }

      const payload = {
        name: profile.name,
        email: profile.email || '',
        location: profile.location || '',
        preferred_location: profile.preferred_location || '',
        experience_years: profile.experience_years ? parseFloat(profile.experience_years) : null,
        education: profile.education || '',
        skills: profile.skills || [],
        department: profile.department || '',
        current_company: profile.current_company || '',
        current_salary: profile.current_salary ? parseFloat(profile.current_salary) : null,
        expected_salary: profile.expected_salary ? parseFloat(profile.expected_salary) : null,
        notice_period_days: profile.notice_period_days ? parseInt(profile.notice_period_days) : null,
        linkedin_url: profile.linkedin_url || '',
      };

      console.log('📤 Updating profile:', payload);

      const response = await axiosInstance.put('/api/candidates/me', payload);
      
      if (response.data.success) {
        setSuccess('Profile updated successfully!');
        
        if (resumeFile) {
          const formData = new FormData();
          formData.append('resume', resumeFile);
          
          const resumeResponse = await axiosInstance.post('/api/candidates/me/resume', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            }
          });
          
          if (resumeResponse.data.success) {
            setSuccess('Profile and resume updated successfully!');
            setResumeFile(null);
            setResumeFileName('');
          }
        }

        localStorage.setItem('userName', profile.name);
        localStorage.setItem('userEmail', profile.email || '');
        localStorage.setItem('userMobile', profile.mobile);

        await fetchProfile();
        setIsEditing(false);
        
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('❌ Error updating profile:', err);
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('token');
    localStorage.removeItem('userMobile');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userRole');
    navigate('/login');
  };

  const formatMobileNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 5) return cleaned;
    return `${cleaned.slice(0, 5)} ${cleaned.slice(5, 10)}`;
  };

  const handleMobileChange = (e) => {
    const raw = e.target.value.replace(/\s/g, '');
    if (raw.length <= 10) {
      setProfile((prev) => ({
        ...prev,
        mobile: raw
      }));
    }
  };

  const getUserInitials = () => {
    if (!profile.name) return 'U';
    const names = profile.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return profile.name.substring(0, 2).toUpperCase();
  };

  const getSkillsDisplay = () => {
    if (!profile.skills || profile.skills.length === 0) return 'No skills added';
    return profile.skills.join(', ');
  };

  if (isFetching) {
    return (
      <div className="profile-loading">
        <div className="spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-card">
        {/* Header */}
        <div className="profile-header">
          <h1>My Profile</h1>
          <div className="profile-header-actions">
            {!isEditing && (
              <button 
                className="edit-btn" 
                onClick={() => setIsEditing(true)}
              >
                <i className="fas fa-pen"></i>
                Edit Profile
              </button>
            )}
          </div>
        </div>

        {/* Profile Avatar */}
        <div className="profile-avatar-section">
          <div className="profile-avatar">
            <span className="avatar-initials">{getUserInitials()}</span>
          </div>
          <h2>{profile.name || 'User'}</h2>
          <p className="profile-role">{userRole === 'admin' ? 'Admin' : userRole === 'super_admin' ? 'Super Admin' : userRole === 'external_admin' ? 'External Admin' : 'Candidate'}</p>
        </div>

        {/* Messages */}
        {error && (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i>
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="success-message">
            <i className="fas fa-check-circle"></i>
            <span>{success}</span>
          </div>
        )}

        {/* Profile Form */}
        <form onSubmit={handleSubmit} className="profile-form">
          <div className="profile-form-grid">
            {/* Basic Info */}
            <div className="form-group">
              <label htmlFor="name">
                <i className="fas fa-user"></i>
                Full Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={profile.name}
                onChange={handleInputChange}
                disabled={!isEditing || isLoading}
                placeholder="Enter your full name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">
                <i className="fas fa-envelope"></i>
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={profile.email}
                onChange={handleInputChange}
                disabled={!isEditing || isLoading}
                placeholder="you@example.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="mobile">
                <i className="fas fa-phone"></i>
                Mobile Number <span className="required">*</span>
              </label>
              <input
                type="tel"
                id="mobile"
                name="mobile"
                value={formatMobileNumber(profile.mobile)}
                onChange={handleMobileChange}
                disabled={!isEditing || isLoading}
                placeholder="00000 00000"
                maxLength="11"
                required
              />
            </div>

            {/* Location */}
            <div className="form-group">
              <label htmlFor="location">
                <i className="fas fa-map-marker-alt"></i>
                Current Location
              </label>
              <input
                type="text"
                id="location"
                name="location"
                value={profile.location}
                onChange={handleInputChange}
                disabled={!isEditing || isLoading}
                placeholder="e.g., Pune"
              />
            </div>

            {/* <div className="form-group">
              <label htmlFor="preferred_location">
                <i className="fas fa-map-pin"></i>
                Preferred Location
              </label>
              <input
                type="text"
                id="preferred_location"
                name="preferred_location"
                value={profile.preferred_location}
                onChange={handleInputChange}
                disabled={!isEditing || isLoading}
                placeholder="e.g., Mumbai"
              />
            </div> */}

            {/* Experience & Education */}
            <div className="form-group">
              <label htmlFor="experience_years">
                <i className="fas fa-briefcase"></i>
                Experience (Years)
              </label>
              <input
                type="number"
                id="experience_years"
                name="experience_years"
                value={profile.experience_years}
                onChange={handleInputChange}
                disabled={!isEditing || isLoading}
                placeholder="e.g., 3"
                min="0"
                step="0.5"
              />
            </div>

            <div className="form-group">
              <label htmlFor="education">
                <i className="fas fa-graduation-cap"></i>
                Education
              </label>
              <input
                type="text"
                id="education"
                name="education"
                value={profile.education}
                onChange={handleInputChange}
                disabled={!isEditing || isLoading}
                placeholder="e.g., B.Tech CSE"
              />
            </div>

            {/* Skills */}
            <div className="form-group full-width">
              <label htmlFor="skills">
                <i className="fas fa-code"></i>
                Skills
              </label>
              <input
                type="text"
                id="skills"
                name="skills"
                value={profile.skills ? profile.skills.join(', ') : ''}
                onChange={handleSkillsChange}
                disabled={!isEditing || isLoading}
                placeholder="React, Node.js, Python (comma separated)"
              />
              {!isEditing && profile.skills && profile.skills.length > 0 && (
                <div className="skills-display">
                  {profile.skills.map((skill, index) => (
                    <span key={index} className="skill-tag">{skill}</span>
                  ))}
                </div>
              )}
            </div>

            {/* Department */}
            <div className="form-group">
              <label htmlFor="department">
                <i className="fas fa-building"></i>
                Department
              </label>
              <input
                type="text"
                id="department"
                name="department"
                value={profile.department}
                onChange={handleInputChange}
                disabled={!isEditing || isLoading}
                placeholder="e.g., Engineering"
              />
            </div>

            {/* Company & Salary */}
            <div className="form-group">
              <label htmlFor="current_company">
                <i className="fas fa-building"></i>
                Current Company
              </label>
              <input
                type="text"
                id="current_company"
                name="current_company"
                value={profile.current_company}
                onChange={handleInputChange}
                disabled={!isEditing || isLoading}
                placeholder="Company name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="current_salary">
                <i className="fas fa-money-bill"></i>
                Current Salary (₹)
              </label>
              <input
                type="number"
                id="current_salary"
                name="current_salary"
                value={profile.current_salary}
                onChange={handleInputChange}
                disabled={!isEditing || isLoading}
                placeholder="e.g., 800000"
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="expected_salary">
                <i className="fas fa-money-bill-wave"></i>
                Expected Salary (₹)
              </label>
              <input
                type="number"
                id="expected_salary"
                name="expected_salary"
                value={profile.expected_salary}
                onChange={handleInputChange}
                disabled={!isEditing || isLoading}
                placeholder="e.g., 1000000"
                min="0"
              />
            </div>

            {/* ✅ Notice Period - Dropdown */}
            <div className="form-group">
              <label htmlFor="notice_period_days">
                <i className="fas fa-clock"></i>
                Notice Period
              </label>
              <select
                id="notice_period_days"
                name="notice_period_days"
                value={profile.notice_period_days}
                onChange={handleSelectChange}
                disabled={!isEditing || isLoading}
                className="notice-period-select"
              >
                {NOTICE_PERIOD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* LinkedIn */}
            <div className="form-group full-width">
              <label htmlFor="linkedin_url">
                <i className="fab fa-linkedin"></i>
                LinkedIn URL
              </label>
              <input
                type="url"
                id="linkedin_url"
                name="linkedin_url"
                value={profile.linkedin_url}
                onChange={handleInputChange}
                disabled={!isEditing || isLoading}
                placeholder="https://linkedin.com/in/your-profile"
              />
            </div>

            {/* Resume */}
            <div className="form-group full-width">
              <label htmlFor="resume">
                <i className="fas fa-file-upload"></i>
                Resume
              </label>
              {profile.resume_url && !isEditing && (
                <div className="resume-current">
                  <a 
                    href={profile.resume_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="resume-download-link"
                  >
                    <i className="fas fa-file-pdf"></i>
                    {profile.resume_filename || 'View Resume'}
                  </a>
                </div>
              )}
              {isEditing && (
                <div className="resume-upload-wrapper">
                  <input
                    type="file"
                    id="resume"
                    name="resume"
                    onChange={handleResumeChange}
                    disabled={isLoading}
                    accept=".pdf,.doc,.docx"
                    className="resume-input"
                  />
                  {resumeFileName && (
                    <div className="resume-selected">
                      <i className="fas fa-check-circle"></i>
                      <span>{resumeFileName}</span>
                      <button
                        type="button"
                        className="resume-remove"
                        onClick={handleRemoveResume}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  )}
                  {profile.resume_filename && !resumeFileName && (
                    <div className="resume-current-edit">
                      <i className="fas fa-file-pdf"></i>
                      <span>Current: {profile.resume_filename}</span>
                      <small>(Upload new file to replace)</small>
                    </div>
                  )}
                  <small>PDF, DOC, DOCX (Max 10MB)</small>
                </div>
              )}
            </div>
          </div>

          {/* Form Actions */}
          {isEditing && (
            <div className="profile-form-actions">
              <button 
                type="button" 
                className="cancel-btn"
                onClick={() => {
                  setIsEditing(false);
                  setError('');
                  setResumeFile(null);
                  setResumeFileName('');
                  fetchProfile();
                }}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="save-btn"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i>
                    Save Changes
                  </>
                )}
              </button>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="profile-footer">
          <p>
            <i className="fas fa-lock"></i>
            Your information is secure and private
          </p>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;